import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRxDatabase } from 'rxdb/plugins/react';
import {
    PERSON_FIELDS,
    formatFindSlipNumber,
    personFieldNames,
    type PersonField
} from '@ast-data/shared';
import type { AppDatabase } from '../../db/database';
import { useActorLabel } from '../users/useActorLabel';
import { useFindingRefLabel } from '../history/findingRefHistoryFields';
import { historyValueFormat } from '../history/historyValueFormats';
import { parcelPickerLabel } from '../findings/parcelPickerLabel';
import { projectLabelText } from '../projects/projectLabelText';
import type { PendingConflict } from './conflictHandler';

/**
 * A conflicting value as a person can judge it.
 *
 * The record stores links as ids, and a choice between two UUIDs is no
 * choice at all. Every link is shown the way the rest of the app names it:
 * an account by its name, a finding reference as "Bef. 7000, Fl. 1, Pl. 1",
 * a parcel, a find, a project by its label. An id this device cannot resolve
 * stays an id — it is all there is to show.
 */

/** A planum or profile is itself named the way a finding reference is. */
const DESCRIPTION_TYPES = ['findingPlanum', 'findingProfile'];

/** Finding references: the find/photo refs and the planum/profile relations. */
const FINDING_REF_KEY = /^(finding|planum|profile)Ids?$|(Planum|Profile)Ids$/;

type IdKind = 'person' | 'findingRef' | 'parcel' | 'find' | 'project';

function idKind(entityType: string, field: string): IdKind | undefined {
    const personIds = (PERSON_FIELDS[entityType] ?? []).map(
        (personField) => personField.id
    );
    if (personIds.includes(field) || field === 'userIds') {
        return 'person';
    }
    if (FINDING_REF_KEY.test(field)) {
        return 'findingRef';
    }
    if (field === 'parcelId' || field === 'parcelIds') {
        return 'parcel';
    }
    if (field === 'findIds') {
        return 'find';
    }
    if (field === 'projectIds') {
        return 'project';
    }
    return undefined;
}

function idsOf(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.map((item) => String(item ?? ''));
    }
    return typeof value === 'string' && value ? [value] : [];
}

/** The ids of one kind both sides of the conflict mention. */
function collectIds(conflict: PendingConflict, kind: IdKind): string[] {
    const ids = new Set<string>();
    for (const fieldConflict of conflict.conflicts) {
        if (idKind(conflict.entityType, fieldConflict.field) !== kind) {
            continue;
        }
        for (const value of [
            fieldConflict.localValue,
            fieldConflict.remoteValue
        ]) {
            idsOf(value)
                .filter(Boolean)
                .forEach((id) => ids.add(id));
        }
    }
    return [...ids];
}

/** Labels of the parcels, finds and projects the conflict links to. */
function useLinkedRecordLabels(conflict: PendingConflict) {
    const db = useRxDatabase() as unknown as AppDatabase | undefined;
    const parcelIds = useMemo(() => collectIds(conflict, 'parcel'), [conflict]);
    const findIds = useMemo(() => collectIds(conflict, 'find'), [conflict]);
    const projectIds = useMemo(
        () => collectIds(conflict, 'project'),
        [conflict]
    );
    const [labels, setLabels] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (
            !db ||
            parcelIds.length + findIds.length + projectIds.length === 0
        ) {
            return;
        }
        let cancelled = false;
        Promise.all([
            db.parcels.findByIds(parcelIds).exec(),
            db.finds.findByIds(findIds).exec(),
            db.projects.findByIds(projectIds).exec()
        ])
            .then(([parcels, finds, projects]) => {
                if (cancelled) {
                    return;
                }
                const next = new Map<string, string>();
                parcels.forEach((parcel, id) =>
                    next.set(id, parcelPickerLabel(parcel))
                );
                finds.forEach((find, id) =>
                    next.set(id, formatFindSlipNumber(find))
                );
                projects.forEach((project, id) =>
                    next.set(id, projectLabelText(project))
                );
                setLabels(next);
            })
            .catch((error: unknown) => {
                // The dialog still works on ids; it just reads worse.
                console.warn('[conflict] linked records not read:', error);
            });
        return () => {
            cancelled = true;
        };
    }, [db, parcelIds, findIds, projectIds]);

    return labels;
}

function plainText(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (Array.isArray(value)) {
        return value.map(plainText).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

export interface ConflictValueText {
    /** One side of a conflicting field, readable. Empty stays empty. */
    text(field: string, value: unknown): string;
    /**
     * One side of a person: each name as written, with its account only where
     * that is called something else — "Gerd (Konto: Gerd Meier), Anna".
     */
    person(personField: PersonField, names: unknown, ids: unknown): string;
    /**
     * What the record itself is called, where only a lookup can say it — a
     * planum as "Bef. 7000, Fl. 1, Pl. 1", a user profile by its account.
     * Empty when it has no such name.
     */
    record: string;
}

export function useConflictValueText(
    conflict: PendingConflict
): ConflictValueText {
    const intl = useIntl();
    const actorLabel = useActorLabel();
    // The family's descriptions are only worth loading when a finding
    // reference is actually in question; '' queries nothing.
    const isDescription = DESCRIPTION_TYPES.includes(conflict.entityType);
    const needsFindingRefs = useMemo(
        () => isDescription || collectIds(conflict, 'findingRef').length > 0,
        [conflict, isDescription]
    );
    const projectId =
        needsFindingRefs && typeof conflict.local.projectId === 'string'
            ? conflict.local.projectId
            : '';
    const findingRefLabel = useFindingRefLabel(projectId);
    const linkedLabels = useLinkedRecordLabels(conflict);

    const text = useCallback(
        (field: string, value: unknown) => {
            const kind = idKind(conflict.entityType, field);
            if (kind) {
                const resolve = (id: string) => {
                    if (kind === 'person') {
                        return actorLabel(id) || id;
                    }
                    if (kind === 'findingRef') {
                        return findingRefLabel(id);
                    }
                    return linkedLabels.get(id) ?? id;
                };
                return idsOf(value).filter(Boolean).map(resolve).join(', ');
            }
            const format = historyValueFormat(conflict.entityType, field);
            if (format) {
                return format(value);
            }
            if (typeof value === 'boolean') {
                return intl.formatMessage({
                    id: value ? 'common.yes' : 'common.no'
                });
            }
            return plainText(value);
        },
        [conflict.entityType, actorLabel, findingRefLabel, linkedLabels, intl]
    );

    const person = useCallback(
        (personField: PersonField, names: unknown, ids: unknown) => {
            const nameList = personFieldNames(personField.shape, names);
            // Positional to the names; a 'single' field holds one id.
            const idList = idsOf(ids);
            const count = Math.max(nameList.length, idList.length);
            const parts: string[] = [];
            for (let i = 0; i < count; i++) {
                const name = (nameList[i] ?? '').trim();
                const id = idList[i] ?? '';
                const account = id ? actorLabel(id) || id : '';
                if (!account || account === name) {
                    if (name) {
                        parts.push(name);
                    }
                    continue;
                }
                parts.push(
                    name
                        ? intl.formatMessage(
                              { id: 'conflict.personAccount' },
                              { name, account }
                          )
                        : account
                );
            }
            return parts.join(', ');
        },
        [actorLabel, intl]
    );

    // Both resolvers answer an unknown id with the id itself: no name, then.
    // A user profile shares its id with the account it belongs to.
    const resolvedRecord = isDescription
        ? findingRefLabel(conflict.local.id)
        : conflict.entityType === 'userProfile'
          ? actorLabel(conflict.local.id)
          : '';
    const record =
        !resolvedRecord || resolvedRecord === conflict.local.id
            ? ''
            : resolvedRecord;

    return useMemo(() => ({ text, person, record }), [text, person, record]);
}
